// 002 — Consulta Externa / Anamnesis-Examen Físico (Anexo 1 MSP, SNS-MSP/HCU-form.002/2021). Fase 7, Tanda 17: bloques B-J
// de las dos hojas (motivo con Primera/Subsecuente, antecedentes con las 10 categorías, enfermedad actual, constantes en 2
// tomas, revisión de órganos y sistemas, examen físico regional y sistémico, 6 diagnósticos PRE/DEF, plan). Datos y
// decisiones en `bloques002Datos.ts` / `bloquesAnamnesisDatos.ts`. Al guardar deja `Encounter.reasonCode`, un `Condition`
// por diagnóstico, el `Observation` de hallazgos y la `Composition` 002 que marca la anamnesis como completada.
import { useMedplum } from '@medplum/react-hooks';
import type { JSX } from 'react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import type { FormularioDiscretoProps } from './index';
import { ErrorGuardado } from './ErrorGuardado';
import { useGuardado } from './useGuardado';
import { Bloque } from './camposSimples';
import { MARCAS_PRE_DEF } from './camposSimplesDatos';
import { SelectorDiagnosticoConMarca } from './MarcaDiagnostico';
import { SeccionCampos } from './SeccionCampos';
import { TablaConColumnas } from './uci/TablaConColumnas';
import { EncabezadoMspCampos, valoresInicialesEncabezadoMsp } from '../EncabezadoMspCampos';
import { useDatosEncabezadoMsp } from '../useDatosEncabezadoMsp';
import {
  CAMPOS_ENFERMEDAD_ANAMNESIS,
  CAMPOS_EXAMEN_REGIONAL,
  CAMPOS_EXAMEN_SISTEMICO,
  CAMPOS_FAMILIARES_ANAMNESIS,
  CAMPOS_PLAN_ANAMNESIS,
  CAMPOS_REVISION_ANAMNESIS,
  codigosDiagnostico,
} from './bloquesAnamnesisDatos';
import { CAMPOS_MOTIVO_002, CAMPOS_PERSONALES_002, COLUMNAS_CONSTANTES_002, estadoInicial002, FILAS_CONSTANTES_002, hallazgosDe002, motivoDe002, paresDe002, tomasDe002, type Estado002 } from './bloques002Datos';
import { guardarRecursosAnamnesis } from './guardadoAnamnesis';

type Clave = 'motivo' | 'personales' | 'familiares' | 'enfermedad' | 'revision' | 'regional' | 'sistemico' | 'plan';

export function Formulario002({ subject, encounter, onGuardado }: FormularioDiscretoProps): JSX.Element {
  const { t } = useTranslation();
  const medplum = useMedplum();
  const [estado, setEstado] = useState<Estado002>(() => estadoInicial002());
  const [campos, setCampos] = useState(() => valoresInicialesEncabezadoMsp());
  const { guardando, error, guardar } = useGuardado();
  const datosEncabezadoMsp = useDatosEncabezadoMsp(subject, campos, '002', t('formularios.f002.titulo'));

  const seccion = (clave: Clave, prefijo: string, lista: Parameters<typeof SeccionCampos>[0]['campos'], columnas: 2 | 3 | 4, filas = 3): JSX.Element => (
    <SeccionCampos prefijoId={`f002-${prefijo}`} campos={lista} valores={estado[clave]} columnas={columnas} filasArea={filas} onChange={(v) => setEstado({ ...estado, [clave]: v })} />
  );

  const handleSubmit = (): Promise<void> =>
    guardar(async () => {
      await guardarRecursosAnamnesis(medplum, {
        codigo: '002',
        subject,
        encounter,
        motivo: motivoDe002(estado),
        diagnosticos: codigosDiagnostico(estado.diagnosticos),
        hallazgos: hallazgosDe002(estado),
        tomas: tomasDe002(estado),
      });
      onGuardado(paresDe002(estado), datosEncabezadoMsp);
    });

  return (
    <div className="flex flex-col gap-6">
      <h3 className="text-base font-medium text-hce-ink">{t('formularios.f002.titulo')}</h3>

      <EncabezadoMspCampos subject={subject} value={campos} onChange={setCampos} />

      <Bloque titulo={t('formularios.f002.motivoTitulo')}>{seccion('motivo', 'mot', CAMPOS_MOTIVO_002, 2, 3)}</Bloque>
      <Bloque titulo={t('formularios.f002.personalesTitulo')}>
        <p className="text-xs text-hce-muted">{t('formularios.f002.describaNota')}</p>
        {seccion('personales', 'per', CAMPOS_PERSONALES_002, 4, 3)}
      </Bloque>
      <Bloque titulo={t('formularios.f002.familiaresTitulo')}>{seccion('familiares', 'fam', CAMPOS_FAMILIARES_ANAMNESIS, 4, 3)}</Bloque>
      <Bloque titulo={t('formularios.f002.enfermedadTitulo')}>
        <p className="text-xs text-hce-muted">{t('formularios.f002.enfermedadNota')}</p>
        {seccion('enfermedad', 'enf', CAMPOS_ENFERMEDAD_ANAMNESIS, 2, 6)}
      </Bloque>
      <Bloque titulo={t('formularios.f002.constantesTitulo')}>
        <p className="text-xs text-hce-muted">{t('formularios.f002.constantesNota')}</p>
        <TablaConColumnas filas={FILAS_CONSTANTES_002} columnas={COLUMNAS_CONSTANTES_002} estado={estado.constantes} onChange={(c) => setEstado({ ...estado, constantes: c })} />
      </Bloque>
      <Bloque titulo={t('formularios.f002.revisionTitulo')}>
        <p className="text-xs text-hce-muted">{t('formularios.f002.marcarNota')}</p>
        {seccion('revision', 'rev', CAMPOS_REVISION_ANAMNESIS, 4, 3)}
      </Bloque>

      <Bloque titulo={t('formularios.f002.examenTitulo')}>
        <p className="text-xs text-hce-muted">{t('formularios.f002.marcarNota')}</p>
        <p className="text-sm font-medium text-hce-ink">{t('formularios.f002.regional')}</p>
        {seccion('regional', 'reg', CAMPOS_EXAMEN_REGIONAL, 4, 3)}
        <p className="text-sm font-medium text-hce-ink">{t('formularios.f002.sistemico')}</p>
        {seccion('sistemico', 'sis', CAMPOS_EXAMEN_SISTEMICO, 4, 3)}
      </Bloque>

      <Bloque titulo={t('formularios.f002.diagnosticoTitulo')}>
        <p className="text-xs text-hce-muted">{t('formularios.f002.diagnosticoNota')}</p>
        {estado.diagnosticos.map((d, i) => (
          <SelectorDiagnosticoConMarca
            key={i}
            label={`${t('formularios.f002.diagnostico')} ${i + 1}`}
            marcaLabel={t('formularios.f002.marcaDiagnostico')}
            marcas={MARCAS_PRE_DEF}
            value={d}
            onChange={(v) => setEstado({ ...estado, diagnosticos: estado.diagnosticos.map((x, j) => (j === i ? v : x)) })}
          />
        ))}
      </Bloque>

      <Bloque titulo={t('formularios.f002.planTitulo')}>
        <p className="text-xs text-hce-muted">{t('formularios.f002.planNota')}</p>
        {seccion('plan', 'pla', CAMPOS_PLAN_ANAMNESIS, 2, 8)}
      </Bloque>

      <Bloque titulo={t('formularios.f002.profesionalTitulo')}>
        <p className="text-sm text-hce-muted">{t('formularios.profesionalNotaCedula')}</p>
      </Bloque>

      <ErrorGuardado error={error} />

      <Button type="button" className="w-fit" onClick={() => void handleSubmit()} disabled={guardando}>
        {t('formulario.guardar')}
      </Button>
    </div>
  );
}
